import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {
  DatePipe,
  TitleCasePipe,
  NgOptimizedImage,
  AsyncPipe,
} from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';

import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';

import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import {
  debounceTime,
  delay,
  filter,
  finalize,
  fromEvent,
  Observable,
  Subject,
  takeUntil,
  throttleTime,
} from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { MeiliQuery } from '../../car-rental';
import { Trip } from 'src/app/shared/classes/trip';
import { ERoutes } from 'src/app/core/enums/routes.enum';
import { EScrollPrefix } from 'src/app/core/enums/scroll-to.enum';
import { EInfoType } from 'src/app/core/enums/country-info-type.enum';
import { fadeInAnimation } from 'src/app/shared/animations/fade-in.animation';

import { TripCardListComponent } from './components/trip-card-list/trip-card-list.component';
import { TripCityInfoComponent } from './components/trip-city-info/trip-city-info.component';
import { OccasionGeniusComponent } from 'src/app/shared/components/occasion-genius/occasion-genius.component';
import { SearchInputFieldComponent } from 'src/app/shared/components/forms/search-input-field/search-input-field.component';

import { CITY_SERVICES } from 'src/app/core/utils/city-services';
import { AuthService } from 'src/app/core/services/auth.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { TripsService } from 'src/app/core/services/trips.service';
import { PackageService } from 'src/app/core/services/package.service';
import { SsrHelperService } from 'src/app/core/services/ssr-helper.service';
import { BreakpointService } from 'src/app/core/services/breakpoint.service';
import { ScrollToIdService } from 'src/app/core/services/scroll-to-id.service';
import { ApiSettingsService } from 'src/app/core/services/api-settings.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { OccasionGeniusService } from 'src/app/core/services/occasion-genius.service';
import { GoogleAnalyticService } from 'src/app/core/services/google-analytic.service';
import { FavoritesProviderService } from 'src/app/core/services/favorites-provider.service';
import { TripWizardStateService } from 'src/app/shared/components/trip-wizard/trip-wizard-state.service';
import { SchedulerBlockNavigationService } from 'src/app/core/services/scheduler-block-navigation.service';
import { HotelsBookingProviderService } from 'src/app/shared/used-components/hotels-booking-container/hotels-booking-provider.service';

import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import {
  IAltDest,
  IMapTripDestination,
  IParsedTripLeg,
  ITripDestination,
  ITripResponse,
} from 'src/app/core/types/interfaces/trip-elements.interface';
import { PIN_COLORS } from 'src/app/shared/constants/map-pin.constant';
import { ETripPlannerView } from 'src/app/core/enums/trip-planner-view.enum';
import { ITrip } from 'src/app/core/types/interfaces/popular-trip.interface';
import { OccasionGeniusResponse } from 'src/app/core/types/models/occasion-genius-response.model';
import { TravellersTransformerPipe } from 'src/app/shared/components/trip-wizard/travellers-transformer.pipe';
import { HOTEL_CLIENT } from '../../../shared/used-components/hotels-booking-container/hotels-booking-container.component';

import { PreviewMapComponent } from 'src/app/shared/maps/preview-map/preview-map.component';
import {
  AuthDialogComponent,
  EAuthPage,
} from 'src/app/shared/dialogs/auth-dialog/auth-dialog.component';
import { TripPreferencesComponent } from './components/trip-preferences/trip-preferences.component';
import { RenameTripDialogComponent } from './dialogs/rename-trip-dialog/rename-trip-dialog.component';
import { SharedDialogComponent } from '../../../shared/dialogs/shared-dialog/shared-dialog.component';
import { AiChatDialogComponent } from 'src/app/shared/components/ai-chat-dialog/ai-chat-dialog.component';
import { BookingBannerComponent } from 'src/app/shared/components/booking-banner/booking-banner.component';
import { OptimizeModeDialogComponent } from './dialogs/optimize-mode-dialog/optimize-mode-dialog.component';
import { PlanningModesDialogComponent } from './dialogs/planning-modes-dialog/planning-modes-dialog.component';
import { TripPlanPrintComponent } from '../../../shared/components/trip-plan-print/trip-plan-print.component';
import { AddDestinationDialogComponent } from './dialogs/add-destination-dialog/add-destination-dialog.component';
import { TripDestinationsListComponent } from './components/trip-destinations-list/trip-destinations-list.component';
import { CarRentalPreviewComponent } from '../../car-rental/components/car-rental-preview/car-rental-preview.component';
import { BookFlightsLinkComponent } from 'src/app/shared/used-components/book-flights-link/book-flights-link.component';
import { PopularItinerariesComponent } from 'src/app/shared/components/popular-itineraries/popular-itineraries.component';
import { EmptyWishlistDialogComponent } from 'src/app/shared/dialogs/empty-wishlist-dialog/empty-wishlist-dialog.component';

import moment from 'moment';

@Component({
  selector: 'app-trip-planner-interaction',
  imports: [
    MatOptionModule,
    MatSelectModule,
    MatSlideToggleModule,
    NgOptimizedImage,
    TranslateModule,
    MatIconModule,
    PreviewMapComponent,
    NgxSkeletonLoaderModule,
    TripPlanPrintComponent,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    TitleCasePipe,
    AsyncPipe,
    TripCardListComponent,
    TripCityInfoComponent,
    BookingBannerComponent,
    OccasionGeniusComponent,
    TripPreferencesComponent,
    BookFlightsLinkComponent,
    CarRentalPreviewComponent,
    SearchInputFieldComponent,
    PopularItinerariesComponent,
    TripDestinationsListComponent,
  ],
  providers: [
    DatePipe,
    {
      provide: HOTEL_CLIENT,
      useClass: HotelsBookingProviderService,
    },
  ],
  templateUrl: './trip-planner-interaction.component.html',
  styleUrl: './trip-planner-interaction.component.scss',
  animations: [fadeInAnimation],
})
export class TripPlannerInteractionComponent implements OnInit, OnDestroy {
  @Input() trip: Trip;
  @Input() tripState: WritableSignal<Trip>;
  @Output() needToRefreshTripState: EventEmitter<void> =
    new EventEmitter<void>();

  public readonly eSvgIcons = ESvgIcons;
  public readonly ETripPlannerView = ETripPlannerView;
  public readonly SERVICES = CITY_SERVICES;

  plannerView: ETripPlannerView = ETripPlannerView.PLANNER_OVERVIEW;
  topTips: ITrip[] = [];
  createdTrip: any;
  cityInfoIndex: number = -1;
  favoritesAmount: number = 0;
  calculatedPaddingBottom: number = 0;
  currentPageActionsCount: number = 0;
  excludedDestinations: { id: number; name: string }[] = [];
  includedDestinations: { id: number; name: string; days: number }[] = [];
  majorDestinations: {
    id: number;
    days: number;
    destIndex: number;
    isFirstLocation: boolean;
    isLastLocation: boolean;
  }[] = [];
  showPreferences: boolean = false;

  isTripsFetching: boolean = false;
  isCreateTripFetching: boolean = true;
  isOccasionGeniusFetching: boolean = false;

  destinationsRoute: IMapTripDestination[] = [];
  legs: IParsedTripLeg[] = [];
  packageResponse: any = {};
  isSpecifyDates: boolean = false;
  isSavingTripProcess: boolean = false;
  isUpdatingByUserInteraction: boolean = false;
  isUndoRedoByUserInteraction: boolean = false;
  userStatusSignal = toSignal(this.userStatus);

  destroy$: Subject<void> = new Subject<void>();

  @ViewChild('topControlEl', { static: true })
  topControlEl: ElementRef<HTMLElement>;
  @ViewChild('destinationBox') destinationBox: ElementRef<HTMLElement>;

  readonly packageService: PackageService = inject(PackageService);
  readonly tripWizardStateService: TripWizardStateService = inject(
    TripWizardStateService,
  );

  protected occasionGeniusResponse = new OccasionGeniusResponse();
  protected carRentalQuery: MeiliQuery | null = null;
  protected readonly eInfoType = EInfoType;
  protected readonly PIN_COLORS = PIN_COLORS;

  #router: Router = inject(Router);
  #matDialog: MatDialog = inject(MatDialog);
  #httpClient: HttpClient = inject(HttpClient);
  #toastService: ToastService = inject(ToastService);
  #ssrHelperService: SsrHelperService = inject(SsrHelperService);
  #apiSettingsService: ApiSettingsService = inject(ApiSettingsService);
  #localStorageService: LocalStorageService = inject(LocalStorageService);

  constructor(
    public readonly cdr: ChangeDetectorRef,
    private readonly datePipe: DatePipe,
    private readonly authService: AuthService,
    protected readonly scrollToIdService: ScrollToIdService,
    protected readonly apiSettingsService: ApiSettingsService,
    private readonly occasionGeniusService: OccasionGeniusService,
    private readonly travellersTransformerPipe: TravellersTransformerPipe,
    private readonly favoritesProviderService: FavoritesProviderService,
    private readonly googleAnalyticService: GoogleAnalyticService,
    private readonly schedulerBlockNavigation: SchedulerBlockNavigationService,
    private readonly tripsService: TripsService,
    private readonly breakpointService: BreakpointService,
  ) {
    if (this.#ssrHelperService.userAgent) {
      if (this.breakpointService.isDesktop()) {
        fromEvent(window, 'scroll')
          .pipe(takeUntilDestroyed(), debounceTime(75), throttleTime(25))
          .subscribe(() => {
            if (this.plannerView !== ETripPlannerView.CITY_OVERVIEW) return;
            const sections = document.querySelectorAll('.location-info');
            const topControlEl: HTMLElement =
              document.querySelector('.overview-box__top');
            const topElOffsetTop = (topControlEl?.offsetTop || 0) + 42;
            sections.forEach((section: HTMLElement, index: number) => {
              if (section.offsetTop < topElOffsetTop) {
                this.setActiveMenuStateIndex(index);
              }
            });
          });
      }

      fromEvent<MouseEvent>(document, 'click')
        .pipe(takeUntilDestroyed(), debounceTime(250))
        .subscribe((event) => {
          const target = event.target as HTMLElement;
          const isButton =
            target.tagName === 'BUTTON' || target.closest('button');

          if (isButton) {
            this.currentPageActionsCount += 1;
            this.#localStorageService.setLocalStorage(
              'currentPageActionsCount',
              this.currentPageActionsCount,
            );
          }
        });

      if (this.schedulerBlockNavigation.tripPlannerBlockIsScheduled) {
        this.isUpdatingByUserInteraction = true;
      }
    }
  }

  @Input() set visibleTripId(id: string) {
    if (id) {
      this.createTripByTripData();
      setTimeout(() => this.scrollToIdService.scrollToTop(), 250);
    }
  }

  get disablePrevCity(): boolean {
    return this.cityInfoIndex === 0;
  }

  get disableNextCity(): boolean {
    if (!this.createdTrip) return true;
    const { destinations } = this.createdTrip;
    if (destinations && destinations.length)
      return this.cityInfoIndex >= destinations.length - 1;
    return true;
  }

  get userId(): number {
    return this.authService.userId;
  }

  get userStatus(): Observable<boolean> {
    return this.authService.isLogged;
  }

  get correctTripName(): string {
    const {
      plannerInfo: { name },
    } = this.tripWizardStateService.currentTripStateValue;

    return name || this.createdTrip?.name;
  }

  get planningTypeText(): string {
    const {
      plannerInfo: { type },
    } = this.tripWizardStateService.currentTripStateValue;
    switch (type) {
      case 'popular':
        return 'Popular Itinerary';
      case 'ai-optimizer':
        return 'AI Generated Itinerary';
      default:
        return 'Classic Itinerary';
    }
  }

  ngOnInit() {
    this.tripWizardStateService.onNewCreateTrip$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.changePlannerView(ETripPlannerView.PLANNER_OVERVIEW);
      });

    this.favoritesProviderService.onUpdateEvent$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateFavouritesCount();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.packageService.stopPollingByDestroy();

    if (this.#ssrHelperService.userAgent) {
      this.#localStorageService.removeItem('currentPageActionsCount');
    }
  }

  handleChangeTrip(cityInfoIndex: number): void {
    this.cityInfoIndex = cityInfoIndex;
    this.changePlannerView(ETripPlannerView.CITY_OVERVIEW);
  }

  changePlannerView(view: ETripPlannerView): void {
    if (view === ETripPlannerView.PLANNER_OVERVIEW) {
      this.cityInfoIndex = -1;
      this.scrollToTop();
    } else if (
      this.cityInfoIndex === -1 &&
      view === ETripPlannerView.CITY_OVERVIEW
    ) {
      this.cityInfoIndex = 0;
      this.setActiveMenuStateIndex(0);
    }

    this.plannerView = view;
    this.calculatedPaddingBottom = 0;
  }

  selectService(index: number): void {
    if (index < 6) {
      this.plannerView = ETripPlannerView.CITY_OVERVIEW;
      if (this.cityInfoIndex === -1) {
        this.cityInfoIndex = 0;
      }
      this.scrollToAnchor(index);
    } else if (index === 6) {
      this.cityInfoIndex = -1;
      this.plannerView = ETripPlannerView.FLIGHTS_BOX;
      this.scrollToTop();
      this.setActiveMenuStateIndex(6);
    } else if (index === 7) {
      this.cityInfoIndex = -1;
      this.plannerView = ETripPlannerView.CAR_RENTAL_BOX;
      this.scrollToTop();
      this.setActiveMenuStateIndex(7);
    }
  }

  setTourismServiceMenuAnalytic(type: string): void {
    this.googleAnalyticService.setTourismServiceMenuAnalytic({
      userID: this.authService.userId,
      tourism_services_menu_type: type as any,
    });
  }

  setParsedTripLegs(legs: IParsedTripLeg[]): void {
    this.legs = legs;
    setTimeout(() => this.cdr.detectChanges(), 250);
  }

  saveTripHandler(): void {
    if (this.userId) {
      this.saveTrip();
      this.googleAnalyticService.setItineraryInteractionAnalytic({
        itinerary_interactions_type: 'save',
        userID: this.userId,
      });
    } else {
      this.#matDialog
        .open(AuthDialogComponent, {
          width: '1150px',
          maxWidth: '98vw',
          maxHeight: '98dvh',
          panelClass: 'mobile-container',
          data: {
            saveEvent: true,
            pageType: EAuthPage.SAVE_EVENT,
          },
        })
        .afterClosed()
        .pipe(delay(250))
        .subscribe((res) => {
          if (!res) return;
          this.saveTrip();
          this.googleAnalyticService.setItineraryInteractionAnalytic({
            itinerary_interactions_type: 'save',
            userID: this.userId,
          });
        });
    }
  }

  goToWishlistHandler(): void {
    const goToWishlist = () => {
      this.googleAnalyticService.setTripTitleStripAnalytic({
        trip_title_strip: 'my wishlist',
        userID: this.authService.userId,
      });

      this.favoritesProviderService
        .getAllFavorites()
        .subscribe((favoritesRes) => {
          if (favoritesRes.success && favoritesRes.data.length) {
            this.#router
              .navigate([ERoutes.SLASH, ERoutes.ACCOUNT, ERoutes.MY_TRIPS])
              .then();
          } else {
            this.#matDialog.open(EmptyWishlistDialogComponent, {
              width: '422px',
              maxWidth: '96vw',
            });
          }
        });
    };

    if (this.userId) {
      goToWishlist();
    } else {
      this.#matDialog
        .open(AuthDialogComponent, {
          width: '1150px',
          maxWidth: '98vw',
          maxHeight: '98dvh',
          panelClass: 'mobile-container',
        })
        .afterClosed()
        .pipe(delay(250))
        .subscribe((res) => {
          if (!res) return;
          this.saveTrip(() => {
            this.googleAnalyticService.setItineraryInteractionAnalytic({
              itinerary_interactions_type: 'save',
              userID: this.userId,
            });
            goToWishlist();
          });
        });
    }
  }

  nextCityInfo(e?: MouseEvent): void {
    if (e) e.stopPropagation();
    if (this.disableNextCity) return;
    this.scrollToAnchor(0);
    this.cityInfoIndex += 1;
  }

  prevCityInfo(e?: MouseEvent): void {
    if (e) e.stopPropagation();
    if (this.disablePrevCity) return;
    this.scrollToAnchor(0);
    this.cityInfoIndex -= 1;
  }

  openAiDialog(): void {
    this.googleAnalyticService.setPlaningTypeStripAnalytic({
      planing_type_strip: 'start AI chat',
      userID: this.authService.userId,
    });
    this.#matDialog
      .open(AiChatDialogComponent, {
        width: '96vw',
        maxWidth: '1920px',
      })
      .afterClosed()
      .subscribe(() => {
        this.googleAnalyticService.setCloseAiPlan({
          userID: this.authService.userId,
        });
      });
  }

  scrollToAnchor(elementId: number): void {
    if (this.#ssrHelperService.userAgent) {
      const zoomLevel = parseFloat(getComputedStyle(document.body).zoom) || 1;
      const calculatedOffset = 264 * zoomLevel;
      setTimeout(() => {
        this.scrollToIdService.scrollTo(
          elementId,
          EScrollPrefix.ELEMENT,
          calculatedOffset,
        );
      }, 250);
    }
  }

  scrollToTop(): void {
    this.scrollToIdService.scrollToTop();
  }

  scrollToBottom(): void {
    this.scrollToIdService.scrollToBottom();
  }

  setBrowserAndEditAnalytic(): void {
    this.googleAnalyticService.setPlaningTypeStripAnalytic({
      planing_type_strip: 'brawse popular iteraries',
      userID: this.authService.userId,
    });
  }

  createTripRequest({ tripStartDate, isSpecifyDates, callback }): void {
    this.packageService.createNewPackage(
      isSpecifyDates ? new Date(tripStartDate) : new Date(),
      'moderate',
      '',
      callback,
    );
  }

  getOccasionGeniusRequest({ placeId, pickupDate, dropoffDate }): void {
    this.occasionGeniusService
      .getOccasionGeniusAll(placeId, {
        from: pickupDate,
        to: dropoffDate,
        offset: 0,
      })
      .subscribe((occasionGeniusResponse) => {
        this.occasionGeniusResponse = new OccasionGeniusResponse(
          occasionGeniusResponse,
        );
        this.isOccasionGeniusFetching = false;
      });
  }

  openRenameTripDialog(): void {
    const tripName = this.correctTripName;
    this.#matDialog
      .open(RenameTripDialogComponent, {
        width: '524px',
        maxWidth: '96vw',
        data: {
          name: tripName,
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.googleAnalyticService.setTripTitleStripAnalytic({
            trip_title_strip: 'rename',
            userID: this.authService.userId,
          });
          this.tripState.update((trip) =>
            trip.updateTrip({
              name: res,
              savesId: '',
              blogUrl: '',
              plannerInfo: {
                type: 'classic',
                name: '',
                description: '',
              },
            }),
          );
          this.updateTripState();
          this.updateTripByUserInteractions();
          this.tripWizardStateService.byUserInteraction = true;
        }
      });
  }

  openAddDestinationDialog(): void {
    const { isSpecifyDates, numDays, tripEndDate, isOptimizedMode, route } =
      this.trip;

    this.#matDialog
      .open(AddDestinationDialogComponent, {
        width: '524px',
        maxWidth: '96vw',
        panelClass: 'mobile-dialog-container',
        data: {
          isSpecifyDates: isSpecifyDates,
          currentTripEndDate: tripEndDate,
          isOptimizedMode: isOptimizedMode,
        },
      })
      .afterClosed()
      .subscribe(
        (body: { res: boolean; maintain: boolean; destination: any }) => {
          if (!body.res) return;
          const { maintain, destination } = body;
          this.includedDestinations = this.includedDestinations.filter(
            (d) => d.id !== destination.placeId,
          );
          this.excludedDestinations = this.excludedDestinations.filter(
            (d) => d.id !== destination.placeId,
          );
          this.includedDestinations.push({
            id: destination.placeId,
            name: destination.name,
            days: destination.days,
          });
          this.tripState.update((trip) =>
            trip.updateTrip({
              savesId: '',
              blogUrl: '',
              plannerInfo: {
                type: 'classic',
                name: '',
                description: '',
              },
              includes: this.includedDestinations,
              excludes: this.excludedDestinations,
            }),
          );
          this.updateTripState();
          if (!isOptimizedMode) {
            route.splice(1, 0, {
              coordinates: '0,0',
              countryId: destination.countryId,
              days: destination.days,
              id: destination.placeId,
              name: destination.name,
            });
          }
          if (!maintain) {
            const newNumDays: number = numDays + destination.days;
            this.tripState.update((trip) =>
              trip.updateTrip({
                numDays: newNumDays,
                tripEndDate: moment(tripEndDate)
                  .add(destination.days, 'days')
                  .toDate(),
              }),
            );
            this.updateTripState();
          }
          this.updateTripByUserInteractions();
          this.tripWizardStateService.byUserInteraction = true;
        },
      );
  }

  openPlaningModeInfoDialog(): void {
    this.#matDialog.open(PlanningModesDialogComponent, {
      width: '422px',
      maxWidth: '96vw',
    });
  }

  togglePlanningModeHandler(ev: MatSlideToggleChange): void {
    this.googleAnalyticService.setItineraryInteractionAnalytic({
      itinerary_interactions_type: ev.checked
        ? 'optimizer on'
        : 'optimizer off',
      userID: this.authService.userId,
    });

    if (!ev.checked) {
      this.showPreferences = false;
      this.tripState.update((trip) =>
        trip.updateTrip({
          savesId: '',
          blogUrl: '',
          isOptimizedMode: false,
          plannerInfo: {
            type: 'classic',
            name: '',
            description: '',
          },
        }),
      );
      this.updateTripState();
      this.updateTripByUserInteractions();
      this.tripWizardStateService.byUserInteraction = true;
      return;
    }

    ev.source.toggle();
    this.#matDialog
      .open(OptimizeModeDialogComponent, {
        width: '422px',
        maxWidth: '96vw',
      })
      .afterClosed()
      .subscribe((confirm: boolean) => {
        if (!confirm) return;
        ev.source.toggle();
        this.tripState.update((trip) =>
          trip.updateTrip({
            savesId: '',
            blogUrl: '',
            isOptimizedMode: true,
            plannerInfo: {
              type: 'classic',
              name: '',
              description: '',
            },
          }),
        );
        this.updateTripState();
        this.updateTripByUserInteractions();
        this.tripWizardStateService.byUserInteraction = true;
      });
  }

  toggleLockDestinationHandler(dest: ITripDestination): void {
    if (dest.locked) {
      this.googleAnalyticService.setItineraryInteractionAnalytic({
        itinerary_interactions_type: 'unpin_destanation',
        userID: this.authService.userId,
      });
      this.includedDestinations = this.includedDestinations.filter(
        (d) => d.id !== dest.id,
      );
    } else {
      this.googleAnalyticService.setItineraryInteractionAnalytic({
        itinerary_interactions_type: 'lock_destanation',
        userID: this.authService.userId,
      });
      this.includedDestinations = this.includedDestinations.filter(
        (d) => d.id !== dest.id,
      );
      this.excludedDestinations = this.excludedDestinations.filter(
        (d) => d.id !== dest.id,
      );
      this.includedDestinations.push({
        id: dest.id,
        name: dest.name,
        days: dest.days,
      });
    }
    this.tripState.update((trip) =>
      trip.updateTrip({
        includes: this.includedDestinations,
        excludes: this.excludedDestinations,
      }),
    );
    this.updateTripState();
    this.updateTripByUserInteractions();
    this.tripWizardStateService.byUserInteraction = true;
  }

  changeDestinationHandler(state: {
    index: number;
    newDest: IAltDest;
    oldDest: ITripDestination;
  }): void {
    const { route, isOptimizedMode } = this.trip;
    const { index, newDest, oldDest } = state;
    this.includedDestinations = this.includedDestinations.filter(
      (d) => d.id !== newDest.id || d.id !== oldDest.id,
    );
    this.excludedDestinations = this.excludedDestinations.filter(
      (d) => d.id !== newDest.id || d.id !== oldDest.id,
    );
    this.includedDestinations.push({
      id: newDest.id,
      name: newDest.name,
      days: oldDest.days,
    });
    this.excludedDestinations.push({
      id: oldDest.id,
      name: oldDest.name,
    });
    this.tripState.update((trip) =>
      trip.updateTrip({
        includes: this.includedDestinations,
        excludes: this.excludedDestinations,
      }),
    );
    this.updateTripState();
    if (!isOptimizedMode) {
      route.splice(index, 1);
      route.splice(index, 0, {
        coordinates: '0,0',
        countryId: newDest.country_id,
        days: oldDest.days,
        id: newDest.id,
        name: newDest.name,
      });
    }
    this.updateTripByUserInteractions();
    this.tripWizardStateService.byUserInteraction = true;
  }

  removeDestinationHandler(res: {
    destination: ITripDestination;
    index: number;
  }): void {
    const { route, isOptimizedMode } = this.trip;
    const { destination, index } = res;
    this.includedDestinations = this.includedDestinations.filter(
      (d) => d.id !== destination.id,
    );
    this.excludedDestinations = this.excludedDestinations.filter(
      (d) => d.id !== destination.id,
    );
    this.excludedDestinations.push({
      id: destination.id,
      name: destination.name,
    });
    this.tripState.update((trip) =>
      trip.updateTrip({
        includes: this.includedDestinations,
        excludes: this.excludedDestinations,
      }),
    );
    this.updateTripState();
    if (!isOptimizedMode) {
      route.splice(index, 1);
    }
    this.updateTripByUserInteractions();
  }

  changeDestinationDurationHandler(state: {
    dest: ITripDestination;
    maintain: boolean;
    numDays: number;
    tripEndDate: Date;
  }): void {
    const { dest, maintain, numDays, tripEndDate } = state;
    const { route, isOptimizedMode } = this.trip;
    if (dest.isFirstLocation || dest.isLastLocation) {
      this.majorDestinations = this.majorDestinations.filter(
        (d) => d.id !== dest.id,
      );
      this.majorDestinations.push({
        id: dest.id,
        days: dest.days,
        destIndex: dest.destIndex,
        isFirstLocation: dest.isFirstLocation,
        isLastLocation: dest.isLastLocation,
      });
    } else {
      this.includedDestinations = this.includedDestinations.filter(
        (d) => d.id !== dest.id,
      );
      this.excludedDestinations = this.excludedDestinations.filter(
        (d) => d.id !== dest.id,
      );
      this.includedDestinations.push({
        id: dest.id,
        name: dest.name,
        days: dest.days,
      });
      this.tripState.update((trip) =>
        trip.updateTrip({
          includes: this.includedDestinations,
          excludes: this.excludedDestinations,
        }),
      );
    }
    if (!isOptimizedMode) {
      route.splice(dest.destIndex, 1, {
        coordinates: dest.coordinates,
        countryId: dest.country_id,
        days: dest.days,
        id: dest.id,
        name: dest.name,
      });
    }
    if (!maintain) {
      this.tripState.update((trip) =>
        trip.updateTrip({
          numDays: numDays,
          tripEndDate: tripEndDate,
          plannerInfo: {
            type: 'classic',
            name: '',
            description: '',
          },
        }),
      );
    }
    this.tripState.update((trip) =>
      trip.updateTrip({
        plannerInfo: {
          type: 'classic',
          name: '',
          description: '',
        },
      }),
    );
    this.updateTripState();
    this.updateTripByUserInteractions();
    this.tripWizardStateService.byUserInteraction = true;
  }

  changeDestinationsHandler(): void {
    this.updateTripByUserInteractions();
  }

  changeTripPreferencesHandler(): void {
    this.googleAnalyticService.setItineraryInteractionAnalytic({
      itinerary_interactions_type: 'prefernces change',
      userID: this.authService.userId,
    });
    this.updateTripByUserInteractions();
    this.tripWizardStateService.byUserInteraction = true;
  }

  public handleUndo(): void {
    this.googleAnalyticService.setItineraryInteractionAnalytic({
      itinerary_interactions_type: 'undo',
      userID: this.authService.userId,
    });
    this.isUndoRedoByUserInteraction = true;
    this.tripWizardStateService.undo();
    this.updateTripByUserInteractions();
  }

  public handleRedo(): void {
    this.googleAnalyticService.setItineraryInteractionAnalytic({
      itinerary_interactions_type: 'redo',
      userID: this.authService.userId,
    });
    this.isUndoRedoByUserInteraction = true;
    this.tripWizardStateService.redo();
    this.updateTripByUserInteractions();
  }

  public printHandle(printElementRef: TripPlanPrintComponent) {
    if (this.userStatusSignal()) {
      printElementRef.tripPlanPrint();
    } else {
      this.#matDialog
        .open(AuthDialogComponent, {
          width: '1150px',
          maxWidth: '98vw',
          maxHeight: '98dvh',
          panelClass: 'mobile-container',
          data: {
            pageType: EAuthPage.LOGIN,
          },
        })
        .afterClosed()
        .pipe(filter(() => this.userStatusSignal()))
        .subscribe(() => printElementRef.tripPlanPrint());
    }
  }

  public shareHandle(): void {
    if (this.userStatusSignal()) {
      this.saveForShare();
    } else {
      this.#matDialog
        .open(AuthDialogComponent, {
          width: '1150px',
          maxWidth: '98vw',
          maxHeight: '98dvh',
          panelClass: 'mobile-container',
          data: {
            pageType: EAuthPage.LOGIN,
          },
        })
        .afterClosed()
        .pipe(filter(() => this.userStatusSignal()))
        .subscribe(() => this.saveForShare());
    }
  }

  public saveForShare(): void {
    this.#httpClient
      .post(this.#apiSettingsService.apiUrl + 'api/trip-planner/save', {
        name: this.trip.name || this.createdTrip.name,
      })
      .subscribe({
        next: (res: any) => {
          if (res.status === 'OK') {
            this.tripsService.getSavedTrip(true, res.data.tripId);
            this.#matDialog.open(SharedDialogComponent, {
              maxWidth: '96vw',
              maxHeight: '96%',
              data: {
                tripInfo: this.trip,
              },
            });
            return;
          }
          this.#toastService.showErrorSnack(res.message);
        },
      });
  }

  public toggleDestinationsExpand(
    destinationBox: HTMLElement,
    sightsOnWay: boolean = false,
  ): void {
    if (!sightsOnWay) {
      destinationBox.classList.toggle('destination-box--simple');
    }

    setTimeout(
      () => {
        const destinationsList: HTMLElement = destinationBox.querySelector(
          'app-trip-destinations-list',
        );

        if (!destinationsList) {
          this.calculatedPaddingBottom = 0;
          return;
        }
        this.calculatedPaddingBottom = Math.abs(
          552 - destinationsList.offsetHeight,
        );
      },
      sightsOnWay ? 150 : 5,
    );
  }

  private saveTrip(callback?: () => void): void {
    this.isSavingTripProcess = true;
    this.#httpClient
      .post(this.#apiSettingsService.apiUrl + 'api/trip-planner/save', {
        name: this.trip.name || this.createdTrip.name,
      })
      .subscribe({
        next: (res: any) => {
          if (res.status === 'OK') {
            this.#toastService.showSuccessSnack(res.message);
            return;
          }
          this.#toastService.showErrorSnack(res.message);
        },
        complete: () => {
          this.isSavingTripProcess = false;
          callback();
        },
      });
  }

  private setCarRentalQuery(params: {
    trip: Trip;
    pickupLocation: {
      latitude: number;
      longitude: number;
    };
    dropoffLocation: {
      latitude: number;
      longitude: number;
    };
  }): void {
    const { trip, pickupLocation, dropoffLocation } = params;
    const { isSpecifyDates, tripStartDate, tripEndDate, numDays } = trip;

    const pickupDate: string = isSpecifyDates
      ? moment(tripStartDate).format('YYYY-MM-DD')
      : moment(new Date()).add(1, 'day').format('YYYY-MM-DD');

    const dropoffDate: string = isSpecifyDates
      ? moment(tripEndDate).format('YYYY-MM-DD')
      : moment(new Date()).add(numDays, 'days').format('YYYY-MM-DD');

    const adults = this.travellersTransformerPipe.getTotalByProperty(
      this.trip.rooms,
      'adults',
    );
    const children = this.travellersTransformerPipe.getTotalByProperty(
      this.trip.rooms,
      'children',
    );

    this.carRentalQuery = {
      pickupLocation,
      dropoffLocation,
      pickupDate,
      pickupTime: '10:00',
      dropoffDate,
      dropoffTime: '10:00',
      driverAge: 30,
      numberOfPassengers: adults + children,
      residency: this.apiSettingsService.countryCode,
    };
  }

  private getTopItinerariesForWidget(params: {
    countries: string;
    type: string;
    days: string;
    startCity: string;
    endCity: string;
  }): Observable<any> {
    const queryParams = new HttpParams()
      .set('countries', params.countries)
      .set('type', params.type)
      .set('days', params.days)
      .set('startCity', params.startCity)
      .set('endCity', params.endCity);

    return this.#httpClient.get(
      this.#apiSettingsService.apiUrl +
        'api/itineraries/getTopItinerariesRoutes',
      {
        params: queryParams,
      },
    );
  }

  private createTripByTripData(): void {
    const tripParams: HttpParams = this.tripParams();

    this.isTripsFetching = true;
    this.isCreateTripFetching = true;
    this.isOccasionGeniusFetching = true;
    this.#httpClient
      .get<ITripResponse>(
        this.#apiSettingsService.apiUrl + 'api/trip-planner/create',
        {
          params: tripParams,
        },
      )
      .pipe(
        finalize(() => {
          this.isCreateTripFetching = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe((res) => {
        if (res && res.destinations) {
          this.createdTrip = res;
          const currentTripRoute = this.setDestinationsRoute(res.destinations);
          this.tripState.update((trip) =>
            trip.updateTrip({
              name: res.name,
              route: currentTripRoute,
            }),
          );
          this.updateTripState();
          this.setQueryByClientInteraction();
          this.checkDestinationBoxExpandState();
          this.tripWizardStateService.updateHistoryStack();

          const { isSpecifyDates, tripStartDate, tripEndDate, numDays } =
            this.trip;
          const countriesIds = new Set(
            res.destinations.map((d) => d.country_id),
          );

          this.favoritesProviderService
            .getAllFavorites()
            .subscribe((favoritesData) => {
              if (favoritesData && favoritesData.data) {
                let favoritesAmount = 0;
                res.destinations.forEach((dest) => {
                  dest.favorites = favoritesData.data.filter(
                    (item) => +item.place_id === dest.id,
                  );
                  favoritesAmount += dest.favorites.length;
                });

                this.favoritesAmount = favoritesAmount;
                this.cdr.markForCheck();
              }
            });
          this.createTripRequest({
            isSpecifyDates,
            tripStartDate,
            callback: (packageResponse: any) => {
              this.packageResponse = packageResponse;
              this.packageService.packages$.next(this.packageResponse.results);
              this.schedulerBlockNavigation.executeNavigation(this);
            },
          });
          this.getTopItinerariesForWidget({
            countries: [...countriesIds].join(','),
            type: 'romantic',
            days: numDays.toString(),
            startCity: res.destinations[0].cityName,
            endCity: res.destinations[res.destinations.length - 1].cityName,
          }).subscribe((popularTripsResponse) => {
            this.topTips = popularTripsResponse;
            this.isTripsFetching = false;
            const pickupDate: number = isSpecifyDates
              ? moment(tripStartDate).unix()
              : moment(new Date()).add(1, 'day').unix();
            const dropoffDate: number = isSpecifyDates
              ? moment(tripEndDate).unix()
              : moment(new Date()).add(numDays, 'days').unix();
            this.getOccasionGeniusRequest({
              placeId: res.destinations[0].id,
              pickupDate,
              dropoffDate,
            });
          });
        }
      });
  }

  private updateFavouritesCount(): void {
    const { route } = this.trip;
    const cityIds = route.map((i: any) => `${i.id}`);

    this.favoritesAmount =
      this.favoritesProviderService.storedFavourites.filter((i) =>
        cityIds.includes(`${i.place_id}`),
      ).length;
    this.cdr.markForCheck();
  }

  private updateTripByUserInteractions(): void {
    this.isUpdatingByUserInteraction = true;
    this.needToRefreshTripState.emit();
    setTimeout(() => {
      const tripParams: HttpParams = this.tripParams();
      this.#httpClient
        .get<ITripResponse>(
          this.#apiSettingsService.apiUrl + 'api/trip-planner/create',
          {
            params: tripParams,
          },
        )
        .pipe(
          finalize(() => {
            this.isUpdatingByUserInteraction = false;
            this.cdr.detectChanges();
          }),
        )
        .subscribe((res) => {
          if (res && res.destinations) {
            this.createdTrip = res;
            this.checkToUpdateTripByClientInteraction(
              res.name,
              res.destinations,
            );
            this.setQueryByClientInteraction();
            this.tripWizardStateService.updateHistoryStack();
            const { isSpecifyDates, tripStartDate } = this.trip;
            this.packageService.createNewPackage(
              isSpecifyDates ? new Date(tripStartDate) : new Date(),
              'moderate',
              '',
              (packageResponse) => {
                this.packageResponse = packageResponse;
                this.packageService.packages$.next(
                  this.packageResponse.results,
                );
              },
            );
          }
        });
    }, 200);
  }

  private checkToUpdateTripByClientInteraction(
    tripName: string,
    destinations: any[],
  ): void {
    const currentTripRoute = this.setDestinationsRoute(destinations);
    if (this.isUndoRedoByUserInteraction) {
      this.isUndoRedoByUserInteraction = false;
      this.tripState.update((trip) =>
        trip.updateTrip({
          name: tripName,
          route: currentTripRoute,
        }),
      );
    } else {
      this.tripState.update((trip) =>
        trip.updateTrip({
          savesId: '',
          blogUrl: '',
          name: tripName,
          route: currentTripRoute,
          plannerInfo: {
            type: 'classic',
            name: '',
            description: '',
          },
        }),
      );
    }
    this.updateTripState();
  }

  private setTripIncludesExcludesDestinations(): void {
    const { includes, excludes } = this.tripState();
    this.includedDestinations = includes;
    this.excludedDestinations = excludes;
  }

  private tripParams(): HttpParams {
    const {
      numDays,
      isSpecifyDates,
      tripStartDate,
      startingCountry,
      startingLabel,
      startingLocationId,
      endingCountry,
      endingLabel,
      endingLocationId,
      isOptimizedMode,
      scores,
      route,
      plannerInfo: { type },
    } = this.trip;
    this.packageResponse = null;
    this.setTripIncludesExcludesDestinations();

    const getDatesString = (): string => {
      if (isSpecifyDates)
        return this.datePipe.transform(tripStartDate, 'mediumDate');
      return '';
    };

    let tripParams: HttpParams = new HttpParams()
      .set('tripType', type)
      .set('days', numDays)
      .set('dates', getDatesString())
      .set('start[id]', startingLocationId)
      .set('start[name]', startingLabel)
      .set('start[countryId]', startingCountry)
      .set('finish[id]', endingLocationId)
      .set('finish[name]', endingLabel)
      .set('finish[countryId]', endingCountry)
      .set('budget', 'moderate')
      .set('transport', 'private')
      .set('requestEngine', 'yes')
      .set('timestamp', new Date().getTime());

    this.excludedDestinations.forEach((destination, index) => {
      tripParams = tripParams
        .append(`excluded[${index}][id]`, destination.id)
        .append(`excluded[${index}][name]`, destination.name);
    });
    this.includedDestinations.forEach((destination, index) => {
      tripParams = tripParams
        .append(`included[${index}][id]`, destination.id)
        .append(`included[${index}][name]`, destination.name)
        .append(`included[${index}][days]`, destination.days);
    });

    if (this.tripWizardStateService.savedTripName) {
      tripParams = tripParams.append(
        'name',
        this.tripWizardStateService.savedTripName,
      );
    }

    if (type === 'popular') {
      tripParams = tripParams.append('temp', 1);
    }

    if (isOptimizedMode) {
      let startDaysDuration = -1;
      let endDaysDuration = -1;

      if (this.majorDestinations.length) {
        this.majorDestinations.forEach((destination) => {
          if (destination.isFirstLocation) {
            startDaysDuration = destination.days;
          }
          if (destination.isLastLocation) {
            endDaysDuration = destination.days;
          }
        });
      }

      tripParams = tripParams
        .append('start[days]', startDaysDuration)
        .append('finish[days]', endDaysDuration);

      for (const key in scores) {
        tripParams = tripParams.append(key, scores[key].value);
      }
    } else {
      const routeString = route.map((r) => `${r.id}:${r.days}`).join(',');
      tripParams = tripParams
        .append('route', routeString)
        .append('start[days]', route[0].days)
        .append('finish[days]', route[route.length - 1].days);
    }

    return tripParams;
  }

  private setQueryByClientInteraction(): void {
    this.setCarRentalQuery({
      trip: this.trip,
      pickupLocation: {
        longitude: this.destinationsRoute[0].coordinates[0],
        latitude: this.destinationsRoute[0].coordinates[1],
      },
      dropoffLocation: {
        longitude:
          this.destinationsRoute[this.destinationsRoute.length - 1]
            .coordinates[0],
        latitude:
          this.destinationsRoute[this.destinationsRoute.length - 1]
            .coordinates[1],
      },
    });
  }

  private setActiveMenuStateIndex(index: number): void {
    const services: NodeListOf<HTMLElement> =
      document.querySelectorAll('div.menu-item');
    if (services && services.length) {
      const activeServiceState: HTMLElement =
        document.querySelector('.menu-active-state');
      const activeServiceEl = services[index];

      setTimeout(() => {
        if (activeServiceEl) {
          activeServiceState.style.setProperty(
            'width',
            `${activeServiceEl.offsetWidth}px`,
          );
          activeServiceState.style.setProperty(
            '--offset-left',
            `${activeServiceEl.offsetLeft}px`,
          );
        }
      }, 100);
    }
  }

  private setDestinationsRoute(destinations: any[]): any[] {
    const currentTripRoutes = [];
    this.destinationsRoute = [];
    this.tripsService.updateTripCount();

    destinations.forEach((destination: any) => {
      const parsedCityCoordinate = destination.coordinates
        .split(',')
        .map((cs: string) => parseFloat(cs.trim()))
        .reverse();
      this.destinationsRoute.push({
        coordinates: parsedCityCoordinate,
        nextLegIsFlight: destination.nextLegIsFlight,
      });

      currentTripRoutes.push({
        coordinates: destination.coordinates,
        countryId: destination.country_id,
        days: destination.days,
        name: destination.name,
        id: destination.id,
      });
    });

    return currentTripRoutes;
  }

  private updateTripState(): void {
    this.tripWizardStateService.currentTripState = new Trip({
      ...this.tripState(),
    });
  }

  private checkDestinationBoxExpandState(): void {
    const { nativeElement } = this.destinationBox;
    if (
      nativeElement &&
      nativeElement.classList.contains('destination-box--simple')
    ) {
      this.toggleDestinationsExpand(nativeElement);
    }
  }
}
